import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FiEye,
    FiCopy,
    FiCheck,
    FiExternalLink,
    FiArrowLeft,
    FiShare2,
    FiZap,
    FiCpu,
    FiStar,
    FiArrowRight,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import clientToast from "../components/Client/clientToast";
import { Title, Meta, Link } from "react-head";

import ClientCalling from "../api/ClientCalling";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import SmartImage from "../components/Client/SmartImage";
import ClientAdSense from "../components/Client/ClientAdSense";
import { formatView } from "../components/formatView";

// Build a deep-link / intent URL for known AI providers that actually accept
// a prompt via query string. For unknown providers we DO NOT mutate the URL —
// instead we copy the prompt to clipboard and let the user paste it.
// Returns { url, embedded } — embedded=true if the prompt was injected.
const buildIntentUrl = (rawUrl, prompt) => {
    if (!rawUrl || !prompt) return { url: rawUrl, embedded: false };
    try {
        const u = new URL(rawUrl);
        const host = u.hostname.toLowerCase();

        // Bing Image Creator â†’ ?q=
        if (host.includes("bing.com") && u.pathname.includes("/images/create")) {
            u.searchParams.set("q", prompt);
            return { url: u.toString(), embedded: true };
        }
        // Google ImageFX (AI Test Kitchen) â†’ ?prompt=
        if (host.includes("aitestkitchen.withgoogle.com")) {
            u.searchParams.set("prompt", prompt);
            return { url: u.toString(), embedded: true };
        }
        // ChatGPT â†’ ?q=  (new format on chatgpt.com & chat.openai.com)
        if (host.includes("chatgpt.com") || host.includes("chat.openai.com")) {
            u.searchParams.set("q", prompt);
            return { url: u.toString(), embedded: true };
        }
        // Perplexity â†’ ?q=
        if (host.includes("perplexity.ai")) {
            u.searchParams.set("q", prompt);
            return { url: u.toString(), embedded: true };
        }

        // Admin-templated URLs: if the configured URL already includes a
        // ?prompt= or ?q= placeholder, replace it with the actual prompt.
        if (u.searchParams.has("prompt")) {
            u.searchParams.set("prompt", prompt);
            return { url: u.toString(), embedded: true };
        }
        if (u.searchParams.has("q")) {
            u.searchParams.set("q", prompt);
            return { url: u.toString(), embedded: true };
        }
    } catch {
        /* malformed URL — fall through */
    }
    // Unknown provider — open as-is; clipboard handles the rest.
    return { url: rawUrl, embedded: false };
};

const CardSkeleton = () => (
    <div className='relative aspect-[5/6] rounded-2xl ios-shimmer overflow-hidden'>
        <div
            className='absolute top-2 left-2 w-10 h-4 rounded-full'
            style={{ background: "rgba(255,255,255,0.08)" }}
        />
        <div className='absolute inset-x-2 bottom-2 hidden md:block'>
            <div
                className='h-9 rounded-lg'
                style={{
                    background: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(8px)",
                }}
            />
        </div>
    </div>
);

const RecommendedCard = ({ item, index, onClick }) => (
    <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{
            duration: 0.5,
            delay: Math.min(index * 0.04, 0.3),
            ease: [0.22, 1, 0.36, 1],
        }}
        whileHover={{ y: -5 }}
        whileTap={{ scale: 0.97 }}
        onClick={onClick}
        className='relative aspect-[5/6] rounded-2xl cursor-pointer group bg-client-back'
        style={{
            overflow: "clip",
            boxShadow:
                "0 10px 26px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)",
        }}
    >
        <SmartImage
            src={item.l_image}
            alt={item.l_name}
            className='absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
        />
        <div
            className='absolute inset-x-0 bottom-0 h-3/5 pointer-events-none'
            style={{
                background:
                    "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 40%, transparent 100%)",
            }}
        />
        <div className='absolute top-2 left-2 ios-glass-dark px-2 py-0.5 rounded-full flex items-center gap-1 text-white text-[9px] md:text-[11px] font-medium pointer-events-none'>
            <FiEye className='text-[10px] md:text-xs' />
            <span className='tabular-nums'>{formatView(item.l_view)}</span>
        </div>
        <div className='absolute inset-x-0 bottom-0 p-2 md:p-3 flex items-center justify-between gap-2'>
            <p
                className='text-white text-[10px] md:text-xs font-semibold truncate flex-1'
                style={{ textShadow: "0 1px 3px rgba(0,0,0,0.85)" }}
            >
                {item.l_name}
            </p>
            <div className='ios-glass-btn hidden md:flex px-2.5 py-1 rounded-full text-[9px] md:text-[10px] font-medium text-white items-center gap-1 whitespace-nowrap'>
                Get Prompt
            </div>
        </div>
        <div
            className='absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300'
            style={{
                boxShadow:
                    "inset 0 0 0 2px rgba(127,0,255,0.5), 0 0 28px rgba(127,0,255,0.35)",
            }}
        />
    </motion.div>
);

const ClientProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [dataInfo, setDataInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [prompt, setPrompt] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [displayedPrompt, setDisplayedPrompt] = useState("");
    const [recommendedItems, setRecommendedItems] = useState([]);
    const [loadingRecommended, setLoadingRecommended] = useState(false);
    const [copied, setCopied] = useState(false);
    const [shared, setShared] = useState(false);
    const promptBoxRef = useRef(null);

    useEffect(() => {
        setPrompt("");
        setDisplayedPrompt("");
        setIsGenerating(false);
    }, [id]);

    useEffect(() => {
        fetchProduct();
    }, [id]);

    useEffect(() => {
        if (dataInfo?.category?._id) fetchRecommendedItems();
    }, [dataInfo]);

    const fetchProduct = async () => {
        try {
            setLoading(true);
            const resp = await ClientCalling?.getProductClientByID(id);
            if (resp?.status) setDataInfo(resp.data);
            else {
                clientToast.error("Data not available");
                setDataInfo(null);
            }
        } catch (err) {
            console.error("Product fetch failed:", err);
            setDataInfo(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchRecommendedItems = async () => {
        try {
            setLoadingRecommended(true);
            const resp = await ClientCalling?.getCategoryClientByID(
                1,
                8,
                dataInfo.category._id
            );
            if (resp?.status && resp?.list) {
                const filtered = resp.list
                    .filter((it) => it._id !== id)
                    .slice(0, 8);
                setRecommendedItems(filtered);
            }
        } catch (err) {
            console.error("Recommended fetch failed:", err);
        } finally {
            setLoadingRecommended(false);
        }
    };

    const handleGeneratePrompt = async () => {
        if (!dataInfo || isGenerating || prompt) return;
        try {
            setIsGenerating(true);
            const resp = await ClientCalling?.getPrompotClientByID(id);
            if (resp?.status && resp?.data?.l_prompt) {
                setPrompt(resp.data.l_prompt);
                typePrompt(resp.data.l_prompt);
                setTimeout(() => {
                    promptBoxRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                    });
                }, 80);
            } else {
                clientToast.error("Prompt not available");
                setIsGenerating(false);
            }
        } catch (err) {
            console.error("Prompt gen failed:", err);
            clientToast.error("Failed to generate prompt");
            setIsGenerating(false);
        }
    };

    const typePrompt = (text) => {
        let index = 0;
        setDisplayedPrompt("");
        const interval = setInterval(() => {
            if (index < text.length) {
                setDisplayedPrompt((prev) => prev + text.charAt(index));
                index++;
            } else {
                clearInterval(interval);
                setIsGenerating(false);
            }
        }, 18);
    };

    const handleCopyPrompt = async () => {
        if (!prompt) return;
        try {
            await navigator.clipboard.writeText(prompt);
            setCopied(true);
            clientToast.success("Prompt copied!");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            clientToast.error("Failed to copy");
        }
    };

    const handleShare = async () => {
        const url = typeof window !== "undefined" ? window.location.href : "";
        try {
            if (navigator.share) {
                await navigator.share({
                    title: dataInfo?.l_name || "PicPromptify",
                    url,
                });
            } else {
                await navigator.clipboard.writeText(url);
                setShared(true);
                setTimeout(() => setShared(false), 1800);
            }
        } catch {}
    };

    // ===== Loading =====
    if (loading) {
        return (
            <div className='min-h-screen dm-sans client-home-bg'>
                <ClientNavbar />
                <FixedButton />
                <div className='mx-auto max-w-7xl px-4 sm:px-6 md:px-8 pt-6 md:pt-10'>
                    <div className='h-9 w-32 ios-shimmer rounded-full mb-6' />
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8'>
                        <div className='aspect-[4/5] rounded-3xl ios-shimmer' />
                        <div className='space-y-4'>
                            <div className='h-3 w-24 ios-shimmer rounded-full' />
                            <div className='h-10 ios-shimmer rounded-md w-3/4' />
                            <div className='flex gap-2'>
                                <div className='h-8 w-24 ios-shimmer rounded-full' />
                                <div className='h-8 w-28 ios-shimmer rounded-full' />
                            </div>
                            <div className='h-14 ios-shimmer rounded-full w-full mt-3' />
                            <div className='h-3 w-32 ios-shimmer rounded-full mt-4' />
                            <div className='grid grid-cols-3 md:grid-cols-4 gap-3 mt-2'>
                                {[...Array(4)].map((_, i) => (
                                    <div
                                        key={i}
                                        className='h-28 rounded-2xl ios-shimmer'
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <ClientFooter />
            </div>
        );
    }

    // ===== Not Found =====
    if (!dataInfo) {
        return (
            <div className='min-h-screen dm-sans client-home-bg'>
                <ClientNavbar />
                <FixedButton />
                <div className='mx-auto max-w-3xl px-4 sm:px-6 md:px-8 py-20 md:py-28 text-center'>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        className='flex flex-col items-center'
                    >
                        <div
                            className='w-20 h-20 rounded-2xl flex items-center justify-center mb-5'
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))",
                                border: "1px solid rgba(255,255,255,0.12)",
                            }}
                        >
                            <FiZap className='text-3xl text-client-title' />
                        </div>
                        <h2 className='text-2xl md:text-3xl font-bold text-client-font tracking-tight'>
                            Prompt not available
                        </h2>
                        <p className='text-client-font/55 text-sm md:text-base mt-2 max-w-md'>
                            The avatar you're looking for doesn't exist or has been removed.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => navigate("/")}
                            className='mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-sm'
                            style={{
                                background:
                                    "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                boxShadow:
                                    "0 14px 32px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                            }}
                        >
                            Back home
                        </motion.button>
                    </motion.div>
                </div>
                <ClientFooter />
            </div>
        );
    }

    const renderSEO = () => (
        <>
            <Title>{`${dataInfo.l_name} | PicPromptify`}</Title>
            <Meta
                name='description'
                content={
                    dataInfo.l_description ||
                    `Explore AI prompt ${dataInfo.l_name} on PicPromptify`
                }
            />
            <Meta
                name='keywords'
                content={`AI prompt, ${dataInfo.l_name}, PicPromptify, ${
                    dataInfo.category?.category_name || ""
                }`}
            />
            <Meta
                property='og:title'
                content={`${dataInfo.l_name} | PicPromptify`}
            />
            <Meta
                property='og:description'
                content={
                    dataInfo.l_description ||
                    `Explore AI prompt ${dataInfo.l_name} on PicPromptify`
                }
            />
            <Meta property='og:type' content='website' />
            <Meta
                property='og:url'
                content={`https://picpromptify.com/list/${id}`}
            />
            <Meta
                property='og:image'
                content={dataInfo.l_image || "/default-product.jpg"}
            />
            <Link
                rel='canonical'
                href={`https://picpromptify.com/list/${id}`}
            />
        </>
    );

    return (
        <div className='min-h-screen dm-sans client-home-bg pb-10'>
            {renderSEO()}
            <ClientNavbar />
            <FixedButton />

            {/* HERO + DETAILS */}
            <section className='relative w-full overflow-hidden'>
                <div
                    aria-hidden
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <div
                    aria-hidden
                    className='absolute top-20 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8 pt-5 md:pt-8'>
                    {/* Top action bar */}
                    <div className='flex items-center justify-between mb-5'>
                        <motion.button
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            whileHover={{ x: -4 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate(-1)}
                            className='ios-glass-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font/85 text-[11px] md:text-xs font-medium'
                        >
                            <FiArrowLeft className='text-sm' />
                            Back
                        </motion.button>

                        <motion.button
                            whileHover={{ y: -2, scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleShare}
                            className='ios-glass-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font text-[11px] md:text-xs font-medium'
                        >
                            {shared ? (
                                <>
                                    <FiCheck style={{ color: "#FCFA02" }} />
                                    Copied
                                </>
                            ) : (
                                <>
                                    <FiShare2 />
                                    Share
                                </>
                            )}
                        </motion.button>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8 items-start'>
                        {/* IMAGE PANEL */}
                        <motion.div
                            initial={{ opacity: 0, y: 24, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{
                                duration: 0.6,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className='md:col-span-6 lg:col-span-5'
                        >
                            <div
                                className='relative rounded-3xl overflow-hidden group'
                                style={{
                                    background: "rgba(18,18,18,0.5)",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    boxShadow:
                                        "0 30px 70px -22px rgba(127,0,255,0.5), 0 14px 32px -10px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.14)",
                                }}
                            >
                                <div
                                    className='relative w-full'
                                    style={{ aspectRatio: "4 / 5" }}
                                >
                                    {/* Blurred bg fill */}
                                    <img
                                        src={dataInfo.l_image}
                                        alt=''
                                        aria-hidden
                                        className='absolute inset-0 w-full h-full object-cover scale-110 pointer-events-none'
                                        style={{
                                            filter: "blur(40px) saturate(1.4)",
                                            opacity: 0.55,
                                        }}
                                    />
                                    {/* Sharp image */}
                                    <SmartImage
                                        src={dataInfo.l_image}
                                        alt={dataInfo.l_name}
                                        eager
                                        className='absolute inset-0 w-full h-full object-contain transition-transform duration-[2s] group-hover:scale-[1.03]'
                                    />
                                    {/* Glass ring */}
                                    <div
                                        aria-hidden
                                        className='absolute inset-0 rounded-3xl pointer-events-none'
                                        style={{
                                            boxShadow:
                                                "inset 0 0 0 1px rgba(255,255,255,0.12)",
                                        }}
                                    />
                                    {/* Views badge */}
                                    <div className='absolute top-3 left-3 ios-glass-dark px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 text-white text-[11px] md:text-xs font-medium'>
                                        <FiEye
                                            className='text-xs'
                                            style={{ color: "#FCFA02" }}
                                        />
                                        <span className='tabular-nums'>
                                            {formatView(dataInfo.l_view)}
                                        </span>
                                    </div>
                                    {/* Category chip */}
                                    {dataInfo.category?.category_name && (
                                        <motion.button
                                            whileHover={{ y: -2, scale: 1.04 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() =>
                                                navigate(
                                                    `/category/${dataInfo.category._id}`
                                                )
                                            }
                                            className='absolute top-3 right-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-[10px] md:text-[11px] font-bold uppercase tracking-wider'
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                                boxShadow:
                                                    "0 8px 22px -6px rgba(127,0,255,0.55)",
                                            }}
                                        >
                                            <HiSparkles className='text-[11px]' />
                                            {dataInfo.category.category_name}
                                        </motion.button>
                                    )}
                                </div>
                            </div>
                        </motion.div>

                        {/* DETAILS PANEL */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.6,
                                delay: 0.1,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className='md:col-span-6 lg:col-span-7 flex flex-col gap-4'
                        >
                            <div>
                                <div className='inline-flex items-center gap-2 ios-glass px-3 py-1 rounded-full text-[10px] md:text-xs text-client-font/85 mb-3'>
                                    <HiSparkles
                                        style={{ color: "#FCFA02" }}
                                    />
                                    <span className='tracking-wider uppercase'>
                                        AI Avatar
                                    </span>
                                </div>
                                <h1 className='text-2xl md:text-4xl lg:text-5xl font-bold text-client-font tracking-tight leading-tight'>
                                    {dataInfo.l_name}
                                </h1>
                                {dataInfo.l_description && (
                                    <p className='text-client-font/65 text-sm md:text-base mt-3 leading-relaxed max-w-2xl'>
                                        {dataInfo.l_description}
                                    </p>
                                )}

                                {/* Stat chips */}
                                <div className='flex flex-wrap items-center gap-2 mt-4'>
                                    <div
                                        className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font text-[11px] md:text-xs font-medium'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(127,0,255,0.05))",
                                            border: "1px solid rgba(127,0,255,0.35)",
                                        }}
                                    >
                                        <FiEye
                                            className='text-xs'
                                            style={{ color: "#FCFA02" }}
                                        />
                                        <span className='tabular-nums font-bold text-client-title'>
                                            {formatView(dataInfo.l_view)}
                                        </span>
                                        <span className='text-client-font/75'>
                                            views
                                        </span>
                                    </div>
                                    {dataInfo.l_model?.length > 0 && (
                                        <div
                                            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-client-font text-[11px] md:text-xs font-medium'
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, rgba(252,250,2,0.18), rgba(252,250,2,0.04))",
                                                border: "1px solid rgba(252,250,2,0.3)",
                                            }}
                                        >
                                            <FiCpu
                                                className='text-xs'
                                                style={{ color: "#FCFA02" }}
                                            />
                                            <span className='tabular-nums font-bold text-client-title'>
                                                {dataInfo.l_model.length}
                                            </span>
                                            <span className='text-client-font/75'>
                                                models
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* CTA BUTTON */}
                            <motion.button
                                onClick={handleGeneratePrompt}
                                disabled={isGenerating || !!prompt}
                                whileHover={
                                    !isGenerating && !prompt
                                        ? { y: -3, scale: 1.02 }
                                        : {}
                                }
                                whileTap={
                                    !isGenerating && !prompt
                                        ? { scale: 0.97 }
                                        : {}
                                }
                                className='relative overflow-hidden rounded-2xl px-5 py-3.5 md:py-4 text-white font-bold text-sm md:text-base inline-flex items-center justify-center gap-2.5 disabled:cursor-not-allowed'
                                style={{
                                    background: prompt
                                        ? "linear-gradient(135deg, #16a34a, #059669)"
                                        : "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                    boxShadow: prompt
                                        ? "0 14px 32px -8px rgba(34,197,94,0.5), inset 0 1px 0 rgba(255,255,255,0.25)"
                                        : "0 14px 32px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                                }}
                            >
                                {/* Sweep shine while idle */}
                                {!isGenerating && !prompt && (
                                    <motion.div
                                        aria-hidden
                                        animate={{ x: ["-120%", "120%"] }}
                                        transition={{
                                            duration: 2.4,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                            repeatDelay: 1.5,
                                        }}
                                        className='absolute inset-y-0 w-1/2 pointer-events-none'
                                        style={{
                                            background:
                                                "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)",
                                            filter: "blur(2px)",
                                        }}
                                    />
                                )}
                                {isGenerating ? (
                                    <>
                                        <div
                                            className='relative w-5 h-5 rounded-full border-[2.5px] border-white/30 border-t-white animate-spin'
                                            style={{ zIndex: 2 }}
                                        />
                                        <span
                                            className='relative tracking-wide uppercase text-sm'
                                            style={{ zIndex: 2 }}
                                        >
                                            Generating…
                                        </span>
                                    </>
                                ) : prompt ? (
                                    <>
                                        <FiCheck className='text-lg relative' />
                                        <span className='relative'>
                                            Prompt Generated
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <motion.div
                                            animate={{
                                                rotate: [0, 8, -8, 0],
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: "easeInOut",
                                            }}
                                            className='relative'
                                        >
                                            <HiSparkles className='text-lg' />
                                        </motion.div>
                                        <span className='relative tracking-wide uppercase text-sm'>
                                            Generate Prompt
                                        </span>
                                        <FiArrowRight className='relative text-sm' />
                                    </>
                                )}
                            </motion.button>

                            {/* PROMPT BOX */}
                            <AnimatePresence>
                                {prompt && (
                                    <motion.div
                                        ref={promptBoxRef}
                                        initial={{ opacity: 0, y: 18, scale: 0.98 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 18 }}
                                        transition={{
                                            duration: 0.5,
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                        className='relative rounded-2xl p-4 md:p-5 overflow-hidden'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, rgba(127,0,255,0.18), rgba(252,250,2,0.04))",
                                            border: "1px solid rgba(127,0,255,0.35)",
                                            backdropFilter:
                                                "blur(18px) saturate(180%)",
                                            WebkitBackdropFilter:
                                                "blur(18px) saturate(180%)",
                                            boxShadow:
                                                "0 16px 36px -12px rgba(127,0,255,0.45), inset 0 1px 0 rgba(255,255,255,0.14)",
                                        }}
                                    >
                                        <div
                                            aria-hidden
                                            className='absolute -top-16 -right-12 w-44 h-44 rounded-full opacity-20 blur-3xl pointer-events-none'
                                            style={{
                                                background:
                                                    "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                                            }}
                                        />
                                        <div className='relative flex items-center justify-between mb-3'>
                                            <div className='inline-flex items-center gap-2'>
                                                <motion.span
                                                    animate={{
                                                        scale: [1, 1.4, 1],
                                                        opacity: [1, 0.5, 1],
                                                    }}
                                                    transition={{
                                                        duration: 1.4,
                                                        repeat: Infinity,
                                                    }}
                                                    className='inline-block w-2 h-2 rounded-full'
                                                    style={{
                                                        background: isGenerating
                                                            ? "#FCFA02"
                                                            : "#22c55e",
                                                        boxShadow: isGenerating
                                                            ? "0 0 8px #FCFA02"
                                                            : "0 0 8px #22c55e",
                                                    }}
                                                />
                                                <p className='text-client-title text-[10px] uppercase tracking-[0.18em] font-semibold'>
                                                    {isGenerating
                                                        ? "Generating"
                                                        : "Generated"}{" "}
                                                    Prompt
                                                </p>
                                            </div>
                                            {!isGenerating && (
                                                <motion.button
                                                    initial={{
                                                        opacity: 0,
                                                        scale: 0.85,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        scale: 1,
                                                    }}
                                                    whileHover={{
                                                        y: -2,
                                                        scale: 1.04,
                                                    }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={handleCopyPrompt}
                                                    className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-semibold'
                                                    style={{
                                                        background:
                                                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                                        boxShadow:
                                                            "0 8px 20px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
                                                    }}
                                                >
                                                    {copied ? (
                                                        <>
                                                            <FiCheck />
                                                            Copied
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiCopy />
                                                            Copy
                                                        </>
                                                    )}
                                                </motion.button>
                                            )}
                                        </div>
                                        <div
                                            className='relative rounded-xl p-3 md:p-4 max-h-60 overflow-y-auto'
                                            style={{
                                                background:
                                                    "rgba(0,0,0,0.35)",
                                                border: "1px solid rgba(255,255,255,0.08)",
                                            }}
                                        >
                                            <p className='text-client-font/90 text-sm md:text-[15px] leading-relaxed whitespace-pre-wrap font-mono'>
                                                {displayedPrompt}
                                                {isGenerating && (
                                                    <motion.span
                                                        animate={{
                                                            opacity: [1, 0, 1],
                                                        }}
                                                        transition={{
                                                            duration: 0.8,
                                                            repeat: Infinity,
                                                        }}
                                                        className='inline-block w-1.5 h-4 ml-0.5 align-middle'
                                                        style={{
                                                            background: "#FCFA02",
                                                            boxShadow:
                                                                "0 0 6px #FCFA02",
                                                        }}
                                                    />
                                                )}
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* AI MODELS */}
                            {dataInfo.l_model?.length > 0 && (
                                <div className='mt-2'>
                                    <div className='flex items-center justify-between mb-3'>
                                        <div className='flex items-center gap-2'>
                                            <div
                                                className='w-1 h-5 rounded-full'
                                                style={{
                                                    background:
                                                        "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                                }}
                                            />
                                            <h3 className='text-client-font font-semibold text-sm md:text-base tracking-wide uppercase inline-flex items-center gap-2'>
                                                Compatible Models
                                                <span className='text-client-title font-bold tabular-nums'>
                                                    {dataInfo.l_model.length}
                                                </span>
                                            </h3>
                                        </div>
                                    </div>
                                    <div className='grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-2.5 md:gap-3'>
                                        {dataInfo.l_model.map((model, mi) => (
                                            <motion.button
                                                key={model._id}
                                                initial={{
                                                    opacity: 0,
                                                    y: 12,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    y: 0,
                                                }}
                                                transition={{
                                                    duration: 0.4,
                                                    delay: mi * 0.04,
                                                }}
                                                whileHover={{
                                                    y: -4,
                                                    scale: 1.04,
                                                }}
                                                whileTap={{ scale: 0.96 }}
                                                onClick={async () => {
                                                    const { url, embedded } =
                                                        buildIntentUrl(
                                                            model.m_url,
                                                            prompt
                                                        );
                                                    if (prompt) {
                                                        try {
                                                            await navigator.clipboard.writeText(
                                                                prompt
                                                            );
                                                            clientToast.success(
                                                                embedded
                                                                    ? `Opening ${model.m_name} with your prompt`
                                                                    : `Prompt copied — paste it into ${model.m_name}`
                                                            );
                                                        } catch {
                                                            /* clipboard blocked */
                                                        }
                                                    }
                                                    window.open(url, "_blank");
                                                }}
                                                className='relative flex flex-col items-center gap-2 p-3 rounded-2xl cursor-pointer group overflow-hidden'
                                                style={{
                                                    background:
                                                        model.recommended
                                                            ? "linear-gradient(135deg, rgba(127,0,255,0.22), rgba(252,250,2,0.06))"
                                                            : "rgba(255,255,255,0.05)",
                                                    border: model.recommended
                                                        ? "1px solid rgba(127,0,255,0.45)"
                                                        : "1px solid rgba(255,255,255,0.1)",
                                                    backdropFilter:
                                                        "blur(14px)",
                                                    boxShadow:
                                                        model.recommended
                                                            ? "0 12px 28px -8px rgba(127,0,255,0.45), inset 0 1px 0 rgba(255,255,255,0.14)"
                                                            : "0 6px 16px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
                                                }}
                                            >
                                                <div className='relative'>
                                                    <div
                                                        className='absolute inset-0 rounded-full blur-md opacity-0 group-hover:opacity-60 transition-opacity'
                                                        style={{
                                                            background: model.recommended
                                                                ? "#7F00FF"
                                                                : "rgba(255,255,255,0.3)",
                                                        }}
                                                    />
                                                    <img
                                                        src={model.m_image}
                                                        alt={model.m_name}
                                                        className='relative w-12 h-12 md:w-14 md:h-14 object-contain rounded-full transition-transform group-hover:scale-110'
                                                        style={{
                                                            border:
                                                                "1px solid rgba(255,255,255,0.18)",
                                                        }}
                                                    />
                                                </div>
                                                <p className='text-client-font text-[10px] md:text-xs font-semibold text-center leading-tight line-clamp-2'>
                                                    {model.m_name}
                                                </p>
                                                <FiExternalLink
                                                    className='absolute top-2 right-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity'
                                                    style={{
                                                        color: "rgba(255,255,255,0.7)",
                                                    }}
                                                />
                                            </motion.button>
                                        ))}
                                    </div>
                                    <p className='text-client-font/45 mt-3 text-[10px] md:text-[11px] italic'>
                                        Note: Results may vary depending on the AI
                                        model used.
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* DSPY1 — between prompt section and recommended items.
                Lazy-loaded, no styling on the ad itself, policy-safe. */}
            <ClientAdSense
                slot='9729451986'
                lazyLoad
                containerClassName='w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-8 md:mt-12'
            />

            {/* RECOMMENDED */}
            {(recommendedItems.length > 0 || loadingRecommended) && (
                <section className='relative w-full px-4 sm:px-6 md:px-8 pt-10 md:pt-16 pb-10'>
                    <div className='mx-auto max-w-7xl'>
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className='flex items-center justify-between mb-5 md:mb-6'
                        >
                            <div className='flex items-center gap-2'>
                                <div
                                    className='w-1 h-6 rounded-full'
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                    }}
                                />
                                <h2 className='text-client-font font-semibold text-sm md:text-base tracking-wide uppercase'>
                                    Recommended for you
                                </h2>
                            </div>
                            {recommendedItems.length > 0 && (
                                <div className='ios-glass px-3 py-1 rounded-full text-client-font/75 text-[10px] md:text-xs inline-flex items-center gap-1.5'>
                                    <HiSparkles
                                        style={{ color: "#FCFA02" }}
                                    />
                                    <span className='tabular-nums font-bold text-client-title'>
                                        {recommendedItems.length}
                                    </span>
                                    <span>picks</span>
                                </div>
                            )}
                        </motion.div>

                        {loadingRecommended ? (
                            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4'>
                                {[...Array(5)].map((_, i) => (
                                    <CardSkeleton key={i} />
                                ))}
                            </div>
                        ) : (
                            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4'>
                                {recommendedItems.map((item, i) => (
                                    <RecommendedCard
                                        key={item._id}
                                        item={item}
                                        index={i}
                                        onClick={() =>
                                            navigate(`/list/${item._id}`)
                                        }
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            <ClientFooter />
        </div>
    );
};

export default ClientProduct;
